import responsiveStore from 'components/Responsive/store';
import { WebGLBounds, WebGLInteractOptions, WebGLOptions, WebGLSceneDefinition, WebGLSceneInterface } from "components/WebGL/gl";
import { getEnv } from "config";
import { each } from "lodash";
import { Kontrol } from "plugins/kontrol";
import interfaceStore from 'plugins/store/interface';
import { Clock, Color, WebGLRenderer } from 'three';
import { WatchStopHandle, computed, watch } from "vue";

let wrapperElement: HTMLElement; // the div which contain the canvas for the webglrenderer (can be specified in the DomGLOptions or the default one is used)
let webglRenderer: WebGLRenderer | null; // the only instance of THREE.WebGLRenderer
let clock: Clock | null; //Clock
let bounds : WebGLBounds;
let activeScenes: WebGLSceneInterface[] = []; // Current Actives scenes
let backupScenes: WebGLSceneDefinition[]; // Save the scenes definition for mobile swapping

//Manage mobile
const isMobile = computed(() => !(interfaceStore.isLayoutDesktop));
let hideForMobile = false;
let clockDelta: number;
let pausedStatus = true;


//Manage resizing
let stopResizeWatcher: WatchStopHandle;

/** 
 * Create/Start the clock
*/
const initClock = () => {
  if (!clock) {
    clock = new Clock();
  }
  else {
    clock.start();
  }
}

// Needs this to check if the renderer can support ktx2 image 
const preInitRenderer = () => {
  if(!webglRenderer) {
    webglRenderer = new WebGLRenderer({
      antialias: true,
    });
    webglRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  return webglRenderer;
}

/** 
 * Create and register a new Dom to WebGL instance and scenes
 * @scenes Array of GLSceneDefinition ( @see GLSceneDefinition )
 * @options optional settings ( @see WebGLOptions )
*/
const useWebGL = (scenes: WebGLSceneDefinition[], options?: WebGLOptions): void => {
  const mergedOptions = {
    clearForMobile: false,
    alpha: false,
    antialias: false,
    bgColor: "#000",
    ...options
  };

  //default bounds (fullscreen)
  bounds = {
    width: { gutter: 0 },
    height: { gutter: 0},
    gutterResponsive: true,
    el: undefined,
    ...options?.sizeBounds
  }

  //save mobile options
  hideForMobile = mergedOptions?.clearForMobile || false;

  //Creation of the WEBGLRenderer instance
  if (!webglRenderer) {
    webglRenderer = new WebGLRenderer({
      antialias: mergedOptions.antialias,
    });
    webglRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  webglRenderer.setClearColor(new Color(mergedOptions.bgColor));
  webglRenderer.setClearAlpha(mergedOptions.alpha ? 0 : 1);
  wrapperElement = mergedOptions.canvasContainer || document.querySelector(".webgl-wrapper")!;

  if (!wrapperElement) throw new Error("options.canvasContainer is missing. Or add a <div class='webgl-wrapper' /> to the root (App.vue) of the project")

  if (!wrapperElement.children.length) {
    wrapperElement.appendChild(webglRenderer.domElement);
  }

  // Add the custom gl scenes
  addScenes(scenes);

  // Continue if only we are not mobile or we allow the mobile to have webgl
  if (!hideForMobile || (hideForMobile && !isMobile.value)) {

    initClock();

    tick(); //start the render loop
  }

  //Manage swap device
  watch(
    () => isMobile.value,
    (mobile) => {
      if (hideForMobile) {
        clock?.stop()
        //stop everything
        if (mobile) {
          clearWebGL();
        }
        // recreate everything because we kill it for mobile
        else {
          addScenes(backupScenes);
          initClock();
          tick();
        }
      }
    }
  )
}

/**
 * Add the required scenes to the DomGL instance
 * @param scenes Array of GLScenesDefinition
 */
const addScenes = (scenes: WebGLSceneDefinition[]) => {
  // Add the custom gl scenes
  activeScenes = [];
  each(scenes, ({ selector, disabled, name, glScene, settings }: WebGLSceneDefinition, index: number) => {
    const scene = new glScene({
      name: name || `scene-${index}`,
      settings: {
        ...settings
      },
      disabled,
      elmts: !selector ? undefined : document.querySelectorAll?.(selector),
    });

    activeScenes.push(scene as WebGLSceneInterface);
  });

  // Resize observer
  stopResizeWatcher = watch(
    () => interfaceStore.viewport,
    () => {
      resize();
    },
    {
      deep: true,
      immediate: true
    }
  );

  backupScenes = scenes.slice();
}

/** 
 * Resize Handler Callback
*/
const resize = () => {
  if (!webglRenderer) return;

  let width = interfaceStore.viewport.width; // fullscreen
  if(bounds.width?.min) width =  Math.max(bounds.width.min, width); // minimum width
  if(bounds.width?.max) width =  Math.min(bounds.width.max, width); // maximum width
  if(bounds.width?.fixed) width = bounds.width.fixed; //fixed width
  if(bounds.width?.gutter) { // remove gutter * 2
    const gutter = bounds.gutterResponsive ?  bounds.width?.gutter * responsiveStore.desktop : bounds.width?.gutter;
    width = (width - gutter * 2);
  }
  
  let height = interfaceStore.viewport.webglHeight; // fullscreen
  if(bounds.height?.min) height =  Math.max(bounds.height.min, height); // minimum height
  if(bounds.height?.max) height =  Math.min(bounds.height.max, height); // maximum height
  if(bounds.height?.fixed) height = bounds.height.fixed; //fixed height
  if(bounds.height?.gutter) { //remove gutter * 2
    const gutter = bounds.gutterResponsive ?  bounds.height?.gutter * responsiveStore.desktop : bounds.height?.gutter;
    height = (height - gutter * 2);
  }

  if(bounds.el) {
    const {width:w, height:h} = bounds.el.getBoundingClientRect();
    width = parseInt(w.toFixed(0));
    height = parseInt(h.toFixed(0));
  }

  webglRenderer.setSize(width, height);

  each(activeScenes, scene => {
    if (scene.visible && !scene.disabled) scene.resize()
  });
}
//receive bgColor and alpha, if no alpha, uses the mergedOptions.alpha
const updateBgColor = (bgColor: string, alpha?: boolean) => {
  webglRenderer?.setClearColor(new Color(bgColor));
  if (alpha) webglRenderer?.setClearAlpha(alpha ? 0 : 1);
}

/** 
 * Return the scene at a specific index on activeScenes
 * @index index number in activeScenes array
*/
const getActiveSceneAt = (index: number): WebGLSceneInterface | null => {
  return activeScenes.length > index ? activeScenes[index] as WebGLSceneInterface : null;
}

/** 
 * Interact the scene at a specific index on activeScenes. Useful if you want to call a method registered in your custom glScene class
 * @index index number in activeScenes array
 * @options Callback name + Callback arguments
*/
const interactWithSceneAt = (index: number, options: WebGLInteractOptions): void => {
  const glScene: any = getActiveSceneAt(index);
  if (glScene && options.callback) glScene[options.callback]?.(options.params);
}


/** 
 * Render Loop
*/
let raf: any;
const tick = () => {
  if (!webglRenderer || !activeScenes) return;
  if (!webglRenderer) return;
  if (!activeScenes) return;

  // Kontrol FPSGraph
  if (getEnv('VITE_KONTROLS')) {
    Kontrol.watchFps("begin");
  }

  each(activeScenes, (glScene) => {
    if (!glScene.disabled) {
      clockDelta = clock?.getDelta() as number

      glScene.render({
        time: clock?.getElapsedTime() as number,
        delta: clockDelta,
      });
    }

    // if scene is visible
    if (glScene.visible) {
      // is scene is not disabled
      if (!glScene.disabled) {

        // effect composer or not?
        if (!glScene.effectComposer) {
          webglRenderer?.render(glScene.getActiveScene(), glScene.getActiveCamera());
        }
        else {
          glScene.effectComposer.render(clock?.getDelta() as number);
        }

        // zIndex configuration needed?
        const zIndex = glScene.settings?.zIndex;
        if (zIndex !== undefined && wrapperElement.style.zIndex !== zIndex) {
          wrapperElement.style.zIndex = zIndex;
        }
      }
    }
  });

  pausedStatus = false;

  // Kontrol FPSGraph
  if (getEnv('VITE_KONTROLS')) {
    Kontrol.watchFps("end");
  }

  raf = requestAnimationFrame(tick);
}

/**
 * Pause the status
 */
const pause = () => {
  if (pausedStatus) return;

  cancelAnimationFrame(raf);
  pausedStatus = true;
}

/** 
 * Clear the GL ressources and scenes
 * Useful if we need to use DOMGL later in the website.
 */
const clearWebGL = (clearBackup = false): void => {
  pause();
  stopResizeWatcher?.();
  each(activeScenes, (scene) => scene.dispose());
  webglRenderer?.clear();
  clock?.stop();
  activeScenes = [];

  if (clearBackup) backupScenes = [];
}

/**
 * Pause the webgl
 * @param value Pause or not
 */
const pauseWebGL = (value: boolean): void => {
  if ((value && pausedStatus) || (!value && !pausedStatus)) return;
  value ? pause() : tick();
}


/** 
 * Dipose and Release the GL ressources and scenes
 * use it only if we don't need DOMGL because the WebGLRenderer is null after that
 */
const releaseWebGL = (): void => {
  console.warn('Don\'t call this method if you still need the mainWebGLRenderer later. Call `clearDomWebGL()` instead');
  clearWebGL();

  wrapperElement.removeChild(wrapperElement.children[0]);
  webglRenderer?.dispose();
  webglRenderer = null;
  activeScenes = [];
  clock = null;
}

export {
  clearWebGL, getActiveSceneAt,
  interactWithSceneAt, clock as mainClock, webglRenderer as mainWebGLRenderer, pauseWebGL, preInitRenderer, releaseWebGL, updateBgColor,
  useWebGL
};

